import { Box, Typography, Button, TextField, InputAdornment, CircularProgress } from "@mui/material";
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from "@react-google-maps/api";
import React, { useState, useCallback, useEffect, useRef } from "react";
import classes from "./address.module.css";
import SearchIcon from "@mui/icons-material/Search";
import {
  AddressMapBackIcon,
  AddressMapFrontIcon,
} from "../../../components/Icons.js";
import { authApi } from "../../../api-calls/user-requests";

const containerStyle = {
  width: "100%",
  height: "400px",
};

// Must be outside component to avoid re-render issues
const LIBRARIES = ["places"];

function AddressMaps(props) {
  const { setIsMapScreen, setInputs, inputs } = props;
  const [marker, setMarker] = useState(null);
  const [addr, setAddr] = useState("");
  const [locality, setLocality] = useState("");
  const [centerPoint, setCenterPoint] = useState({ lat: 20.5937, lng: 78.9629 });
  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);

  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries: LIBRARIES,
  });

  const onUnmount = useCallback(function callback() {}, []);

  const getLocalityFromComponents = (addressComponents, formattedAddress) => {
    const localityTypes = [
      "locality",
      "sublocality_level_1",
      "sublocality",
      "neighborhood",
      "administrative_area_level_3",
      "administrative_area_level_2",
    ];
    for (const type of localityTypes) {
      const comp = addressComponents.find((c) => c.types.includes(type));
      if (comp) return comp.long_name;
    }
    return formattedAddress ? formattedAddress.split(",")[0].trim() : "Unknown locality";
  };

  const getGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const map_url = `https://www.google.com/maps?q=${lat},${lng}`;
        setLocality(
          getLocalityFromComponents(result.address_components, result.formatted_address)
        );
        setAddr(result.formatted_address);
        setInputs({
          ...inputs,
          maplink: map_url,
          lat,
          lng,
        });
      }
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  const placeMarker = (lat, lng) => {
    setMarker({ lat, lng });
    setCenterPoint({ lat, lng });
    getGeocode(lat, lng);
  };

  const handleClick = (event) => {
    placeMarker(event.latLng.lat(), event.latLng.lng());
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const map_url = `https://www.google.com/maps?q=${lat},${lng}`;
    setMarker({ lat, lng });
    setCenterPoint({ lat, lng });
    if (place.address_components) {
      setLocality(
        getLocalityFromComponents(place.address_components, place.formatted_address)
      );
      setAddr(place.formatted_address || "");
      setInputs({ ...inputs, maplink: map_url, lat, lng });
    } else {
      getGeocode(lat, lng);
    }
  };

  const extractCoordsFromUrl = (url) => {
    const patterns = [
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
    ];
    for (const pattern of patterns) {
      const m = url.match(pattern);
      if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    }
    return null;
  };

  const handleUrlChange = async (value) => {
    setUrlInput(value);
    const trimmed = value.trim();
    if (!trimmed) return;

    if (trimmed.includes("maps.app.goo.gl") || trimmed.includes("goo.gl/maps")) {
      setUrlLoading(true);
      try {
        const res = await authApi.post("/locations/resolve-short-url", { shortUrl: trimmed });
        if (res.data.success && res.data.coordinates) {
          const { latitude, longitude } = res.data.coordinates;
          placeMarker(latitude, longitude);
        }
      } catch (e) {
        console.error("Error resolving short URL:", e);
      }
      setUrlLoading(false);
      return;
    }

    const coords = extractCoordsFromUrl(trimmed);
    if (coords) {
      placeMarker(coords.lat, coords.lng);
    }
  };

  useEffect(() => {
    if (inputs?.lat && inputs?.lng) {
      placeMarker(inputs.lat, inputs.lng);
    } else if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenterPoint({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {}
      );
    }
  }, []);

  return (
    <Box>
      <Box className={classes.add_map_header}>
        <span onClick={() => setIsMapScreen(false)}>
          <AddressMapBackIcon />
        </span>
        <Typography>Customer Location</Typography>
        <span>
          <AddressMapFrontIcon />
        </span>
        <Typography>Add from Map</Typography>
      </Box>

      {/* Search box with Places Autocomplete */}
      {isLoaded && (
        <Box sx={{ mb: 1 }}>
          <Autocomplete
            onLoad={(ref) => (autocompleteRef.current = ref)}
            onPlaceChanged={handlePlaceChanged}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search for a location..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Autocomplete>
        </Box>
      )}

      {/* Map */}
      <Box position={"relative"}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={centerPoint}
            zoom={10}
            onUnmount={onUnmount}
            onClick={handleClick}
          >
            {marker && <Marker position={{ lat: marker.lat, lng: marker.lng }} />}
          </GoogleMap>
        ) : (
          <Typography>Loading Map...</Typography>
        )}
        <Box className={classes.add_location_box}>
          <h5>{locality || "n/a"}</h5>
          <span>{addr || "n/a"}</span>
          <Button
            variant="contained"
            onClick={() => setIsMapScreen(false)}
            disabled={!marker}
          >
            Add Location
          </Button>
        </Box>
      </Box>

      {/* Google Maps URL paste */}
      <Box sx={{ mt: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Or paste a Google Maps link (maps.app.goo.gl/...)"
          value={urlInput}
          onChange={(e) => handleUrlChange(e.target.value)}
          InputProps={{
            endAdornment: urlLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
    </Box>
  );
}

export default AddressMaps;
